import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Checkbox,
  cn,
  Loading,
  Modal,
  notify,
  ScrollableContainer,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { FeatureName } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { ZipTransformer } from '@blocksuite/affine/blocks';
import { noop } from '@blocksuite/affine/global/utils';
import {
  useLiveData,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import {
  getFileImportBoard,
  useGetBoards,
  useIsAuthorized,
  useLogout,
} from './api';
import { type Board, miroAtom } from './model';
import { signin } from './utils';

export const MiroPanel = () => {
  const t = useI18n();

  const { workspaceService, workspaceQuotaService } = useServices({
    WorkspaceService,
    WorkspaceQuotaService,
  });
  const docCollection = workspaceService.workspace.docCollection;

  const quota = useLiveData(workspaceQuotaService.quota.quota$);
  const max = useLiveData(workspaceQuotaService.quota.max$);
  const used = useLiveData(workspaceQuotaService.quota.used$);

  const [{ openModal }, setMiroAtom] = useAtom(miroAtom);

  const [selectedBoards, setSelectedBoards] = useState<Board[]>([]);
  const [isPendingImport, setIsPendingImport] = useState<boolean>(false);

  const isAuthorizedQuery = useIsAuthorized({ query: { enabled: openModal } });
  const boardsQuery = useGetBoards({
    query: { enabled: openModal && !isPendingImport },
  });
  const logoutMutation = useLogout();

  const isAllSelected = useMemo(
    () => selectedBoards.length === boardsQuery.data?.data?.items?.length,
    [selectedBoards, boardsQuery.data]
  );

  const titleSelect = useMemo(
    () =>
      isAllSelected
        ? t['docnboard.miro.panel.import.deselect-all']()
        : t['docnboard.miro.panel.import.select-all'](),
    [isAllSelected, t]
  );

  const closeModal = useCallback(() => {
    if (isPendingImport) return;

    setMiroAtom(prev => ({ ...prev, openModal: false }));
    setIsPendingImport(false);
  }, [setMiroAtom, setIsPendingImport, isPendingImport]);

  const cleanSelectedBoards = useCallback(() => {
    setSelectedBoards([]);
  }, [setSelectedBoards]);

  const toggleAllBoards = useCallback(() => {
    setSelectedBoards([
      ...(isAllSelected ? [] : (boardsQuery.data?.data?.items ?? [])),
    ]);
  }, [setSelectedBoards, isAllSelected, boardsQuery.data]);

  const onLogin = useCallback(() => {
    closeModal();
    signin();
  }, [closeModal]);

  const onLogout = useAsyncCallback(async () => {
    await logoutMutation.mutateAsync();

    onLogin();
  }, [logoutMutation, onLogin]);

  const onImportBoard = useAsyncCallback(async () => {
    if (
      used.usedDocCount + selectedBoards.length > max.docLimit &&
      quota?.name !== FeatureName.unlimited
    ) {
      workspaceQuotaService.dialog.open();
      return;
    }

    let isErrorImport = false;
    setIsPendingImport(true);
    for (const board of selectedBoards) {
      try {
        const file = await getFileImportBoard(board);
        await ZipTransformer.importDocs({
          collection: docCollection,
          imported: file,
        });
      } catch (error) {
        isErrorImport = true;
        if (error instanceof Error) {
          notify.error({
            title: t['docnboard.miro.import.error.title'](),
            message: error.message,
          });
        }
      }
    }
    setIsPendingImport(false);

    if (isErrorImport) return;

    cleanSelectedBoards();
    closeModal();
  }, [
    used.usedDocCount,
    selectedBoards,
    max,
    quota,
    cleanSelectedBoards,
    closeModal,
    workspaceQuotaService.dialog,
    docCollection,
    t,
  ]);

  const toggleSelectedBoard = useCallback(
    (board: Board) => {
      setSelectedBoards(prev => {
        const idx = prev.findIndex(b => b.boardId === board.boardId);
        if (idx !== -1) {
          prev.splice(idx, 1);
        } else {
          prev.push(board);
        }

        return [...prev];
      });
    },
    [setSelectedBoards]
  );

  return (
    <Modal
      open={openModal}
      width={1280}
      height={920}
      contentOptions={{
        className:
          'flex !p-0 overflow-hidden md:max-w-2xl xl:max-w-6xl max-w-xs !max-h-[85vh]',
      }}
      onOpenChange={closeModal}
      withoutCloseButton={isPendingImport}
    >
      <div className="w-full flex flex-col p-10 space-y-4">
        <h1 className="text-2xl font-bold mb-1">
          {t['docnboard.miro.panel.import.title']()}
        </h1>
        <ScrollableContainer scrollableContainerClassName="pr-5">
          {boardsQuery.isPending ? (
            <div className="w-full h-full flex justify-center items-center">
              <Loading size="5rem" />
            </div>
          ) : (
            <div className="h-full flex flex-col justify-between gap-4">
              <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {isPendingImport && (
                  <div className="absolute w-full h-full z-50 bg-white/80"></div>
                )}
                {boardsQuery.data?.data?.items?.map(board => {
                  return (
                    <Card
                      key={board.boardId}
                      onClick={() => toggleSelectedBoard(board)}
                      className={cn(
                        'relative hover:cursor-pointer m-1 outline-none hover:outline-[--affine-primary-color]',
                        {
                          'outline outline-[--affine-primary-color]':
                            selectedBoards.some(
                              b => b.boardId === board.boardId
                            ),
                        }
                      )}
                    >
                      <Checkbox
                        className="!absolute top-1 left-1 text-2xl text-black"
                        checked={selectedBoards.some(
                          b => b.boardId === board.boardId
                        )}
                        onChange={noop}
                      />
                      <div className="w-full h-48 rounded-t-lg">
                        {board.picture.imageURL && (
                          <img
                            src={board.picture.imageURL}
                            alt="Cover"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <CardHeader>
                        <CardTitle className="overflow-hidden text-ellipsis">
                          {board.name}
                        </CardTitle>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </ScrollableContainer>
        <div className="flex justify-center flex-col-reverse md:justify-between xl:justify-between md:flex-row xl:flex-row gap-2">
          <div className="flex flex-row flex-col">
            {isAuthorizedQuery.data?.data?.isAuthorized && (
              <Button
                variant="secondary"
                className="h-8 w-8 p-0"
                size="large"
                disabled={isPendingImport}
                onClick={onLogout}
              >
                <span>{t['docnboard.miro.change-team.title']()}</span>
              </Button>
            )}
          </div>
          <div className="flex flex-row flex-col-reverse md:flex-row xl:flex-row gap-2">
            <Button
              variant="secondary"
              className="h-8 w-8 p-0"
              size="large"
              onClick={toggleAllBoards}
              disabled={isPendingImport}
            >
              <span>{titleSelect}</span>
            </Button>
            <Button
              variant="primary"
              className="h-8 w-8 p-0"
              size="large"
              onClick={onImportBoard}
              disabled={selectedBoards.length === 0}
              loading={isPendingImport}
            >
              <span>{t['docnboard.miro.panel.import-button']()}</span>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
